import { UiStack } from '@experiences/ui-common';
import {
    ApTextField,
    ApTreeView,
    PortalIcon,
} from '@uipath/portal-shell-react';
import type { ApTreeViewItem } from '@uipath/portal-shell-types';
import React, {
    useCallback,
    useEffect,
    useState,
} from 'react';
import { useIntl } from 'react-intl';

import type { Folder } from '../../../services/orchestrator/OrchFolderService';
import {
    FolderType,
    getAllFolders,
    getAllFoldersForCurrentUserV2,
} from '../../../services/orchestrator/OrchFolderService';

type IOrchestratorFolderTreeViewProps = {
    label?: string;
    organizationId: string;
    tenantId: string;
    isAllFolders?: boolean;
    includePersonal?: boolean;
    onChange?: (folder?: Folder) => void;
    externallyControlled?: boolean;
    selectedFolder?: Folder;
};

function buildHierarchy(items: Folder[], noAccessMessage: string, isAllFolders = false) {
    // Create a map of items by ID for quick access
    const itemMap = new Map();
    items.forEach(item => {
        (item as any).children = []; // Initialize the children array for each item
        itemMap.set(item.Id, item);
    });

    // Create the root array that will hold top-level items
    const hierarchy: any[] = [];

    items.forEach(item => {
        if (item.ParentId) {
            // If item has a parent, add it to the parent's children array
            const parent = itemMap.get(item.ParentId);
            if (parent) {
                parent.children.push(item);
            }
        } else {
            // If no parent, add item to the root level
            hierarchy.push(item);
        }
    });

    const mapFolderToTreeViewItem = (folder: any): ApTreeViewItem => ({
        id: folder.Key,
        title: folder.DisplayName,
        titleColor: !folder.IsSelectable && !isAllFolders ? 'var(--color-foreground-light)' : '',
        children: folder.children ? folder.children.map(mapFolderToTreeViewItem) : undefined,
        description: !folder.IsSelectable && !isAllFolders ? noAccessMessage : folder.DisplayName,
        icon: 'Folder' as any,
    });

    const result: ApTreeViewItem[] = hierarchy.map(mapFolderToTreeViewItem);
    return result;
}

export const OrchestratorFolderTreeView = ({
    label, organizationId, tenantId, isAllFolders, includePersonal, onChange, externallyControlled, selectedFolder,
}: IOrchestratorFolderTreeViewProps) => {
    const { formatMessage: translate } = useIntl();
    const [ folderKey, setFolderKey ] = useState<string | undefined>();
    const [ treeViewItems, setTreeViewItems ] = useState<ApTreeViewItem[]>([]);
    const [ searchQuery, setSearchQuery ] = useState<string>('');
    const [ filteredItems, setFilteredItems ] = useState<ApTreeViewItem[]>([]);
    const [ allFolders, setAllFolders ] = useState<Folder[]>([]);

    useEffect(() => {
        if (organizationId && tenantId) {
            const fetchFolders = isAllFolders
                ? getAllFolders({
                    accountLogicalName: organizationId,
                    tenantName: tenantId,
                }, {}).then(data => data.value)
                : getAllFoldersForCurrentUserV2(tenantId);

            fetchFolders
                .then((data) => data.filter(folder => !!includePersonal || folder.FolderType !== FolderType.Personal))
                .then((data) => {
                    const items: ApTreeViewItem[] = buildHierarchy(data, translate({ id: 'CLIENT_CG_FOLDER_NO_ACCESS' }), isAllFolders);
                    const shared = items.find(item => item.title === 'Shared');
                    setAllFolders(data);
                    setTreeViewItems(items);
                    setFilteredItems(items);
                    if (shared && !folderKey && !externallyControlled) {
                        setFolderKey(shared.id);
                        onChange?.(data.find(folder => folder.Key === shared.id));
                    }
                });
        }
    }, [ organizationId, tenantId, includePersonal, translate, externallyControlled, onChange, folderKey, isAllFolders ]);

    useEffect(() => {
        if (!externallyControlled) {
            return;
        }

        setFolderKey(selectedFolder?.Key);
    }, [ externallyControlled, selectedFolder ]);

    useEffect(() => {
        if (searchQuery) {
            const lowerCaseQuery = searchQuery.toLowerCase();
            const filterItems = (items: ApTreeViewItem[]): ApTreeViewItem[] => items
                .map(item => ({
                    ...item,
                    children: item.children ? filterItems(item.children) : [],
                }))
                .filter(item => item.title.toLowerCase().includes(lowerCaseQuery) || (item.children && item.children.length > 0));
            setFilteredItems(filterItems(treeViewItems));
        } else {
            setFilteredItems(treeViewItems);
        }
    }, [ searchQuery, treeViewItems ]);

    const handleSelectedItemChanged = useCallback((event: CustomEvent<string>) => {
        const selectedKey = event.detail;
        setFolderKey(selectedKey);
        const selectedFolderByKey = allFolders.find(item => item.Key === selectedKey);
        onChange?.(selectedFolderByKey);
    }, [ allFolders, onChange ]);

    return (
        <UiStack
            h="100%"
            direction='column'
        >
            <ApTextField
                id="folder-search"
                startAdornment={<PortalIcon
                    style={{ marginRight: '8px' }}
                    name='search'
                />}
                data-cy="folder-search"
                label={label ?? 'Folder'}
                placeholder="Search"
                value={searchQuery}
                onValueChanged={(e) => setSearchQuery(e.detail)}
            />
            <UiStack
                direction='column'
                h="100%"
                style={{ overflow: 'auto' }}>
                <ApTreeView
                    id="folders-tree-view"
                    data-cy="folders-tree-view"
                    items={filteredItems}
                    onSelectedItemChanged={handleSelectedItemChanged}
                    selectedItemId={externallyControlled ? selectedFolder?.Key : folderKey}
                />
            </UiStack>
        </UiStack>
    );
};

